import { useRef, useState } from 'react'
import { useModal } from '../../contexts/ModalContext'
import Button from '../primitives/Button'
import Overlay, { OverlayCard } from '../primitives/Overlay'
import AppIcon from '../icons/AppIcon'
import styles from './GateScreen.module.css'

interface GateSubmitOverlayProps {
  onClose: () => void
  onSubmit: (file: File) => Promise<void>
  isSubmitting: boolean
}

export default function GateSubmitOverlay({
  onClose,
  onSubmit,
  isSubmitting,
}: GateSubmitOverlayProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const { showSnackbar } = useModal()

  function handlePick(selected: File | null) {
    if (!selected) return
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setFile(selected)
    setPreviewUrl(URL.createObjectURL(selected))
  }

  async function handleSubmit() {
    if (!file) {
      showSnackbar('gate-pick', 'Choose a photo or video first', 'warning')
      return
    }
    try {
      await onSubmit(file)
      onClose()
    } catch {
      showSnackbar('gate-submit-error', 'Could not submit proof. Try again.', 'error')
    }
  }

  return (
    <Overlay variant="backdrop" onClose={onClose}>
      <OverlayCard>
        <div className={styles.eyebrow}>Submit proof</div>
        <h2 className={styles.headTitle} style={{ fontSize: 24, marginTop: 8 }}>
          Photo or video
        </h2>
        <p className={styles.headSub}>Show your team you completed the challenge.</p>

        <button
          type="button"
          className={styles.pickArea}
          onClick={() => inputRef.current?.click()}
        >
          <AppIcon icon="mdi:camera-plus" size={28} />
          <div style={{ marginTop: 10 }}>Tap to choose from your library</div>
        </button>

        <input
          ref={inputRef}
          className={styles.hiddenInput}
          type="file"
          accept="image/*,video/*"
          onChange={(e) => handlePick(e.target.files?.[0] ?? null)}
        />

        {previewUrl && (
          <div className={styles.preview}>
            {file?.type.startsWith('video/') ? (
              <video src={previewUrl} controls playsInline />
            ) : (
              <img src={previewUrl} alt="Preview" />
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
          <Button variant="blue" onClick={handleSubmit} disabled={isSubmitting || !file}>
            {isSubmitting ? 'Submitting…' : 'Submit proof'}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </OverlayCard>
    </Overlay>
  )
}
